// Search every drawing's printed text, and copy a sheet's text in reading order.
//
// Pure: callers feed it the text items read from each PDF page
// (pdfRender.extractPageText — viewport points, origin bottom-left) and get back
// lines, hits and plain text. No OCR: a scanned sheet has no text layer, and is
// reported as such rather than silently skipped.

import { toIndexedLines } from "./textLines";

const norm = (s) => String(s ?? "").toLowerCase();

/** A sheet page's text as plain text, top to bottom, left to right. */
export function readingOrderText(items) {
  return toIndexedLines(items).map(l => l.text).join("\n");
}

/**
 * Index entries for one page: one per visual line.
 *   sheet — { sheetId, label }   page — 1-based   pageSize — { width, height }
 */
export function buildEntries({ sheetId, label, page, items, pageSize }) {
  return toIndexedLines(items).map(l => ({
    sheetId, label, page, text: l.text, lower: norm(l.text), x: l.x, y: l.y, parts: l.parts,
    pageW: pageSize?.width ?? 0, pageH: pageSize?.height ?? 0,
  }));
}

const MAX_TOKENS = 8;
const tokensOf = (q) => norm(q).trim().split(/\s+/).filter(Boolean).slice(0, MAX_TOKENS);
const isWordChar = (ch) => !!ch && /[a-z0-9]/i.test(ch);

function wordBoundaryHit(lower, token) {
  let i = lower.indexOf(token);
  while (i !== -1) {
    if (!isWordChar(lower[i - 1]) && !isWordChar(lower[i + token.length])) return true;
    i = lower.indexOf(token, i + 1);
  }
  return false;
}

/**
 * Lines containing EVERY word of the query (any order), best first: the whole
 * phrase beats separate words, whole-word matches beat partial ones, shorter lines
 * beat long ones. Returns [{ entry, start, end, x, y, score }] where x, y locate the
 * matched word on the page (viewport points, origin bottom-left).
 */
export function searchEntries(entries, query, { limit = 100 } = {}) {
  const tokens = tokensOf(query);
  if (!tokens.length) return [];
  const phrase = tokens.join(" ");
  const hits = [];
  for (const e of entries) {
    if (!tokens.every(t => e.lower.includes(t))) continue;
    let score = 0;
    if (tokens.length > 1 && e.lower.includes(phrase)) score += 100;
    for (const t of tokens) if (wordBoundaryHit(e.lower, t)) score += 10;
    score -= Math.min(e.lower.length, 400) / 1000; // prefer a tight line over a paragraph
    const start = tokens.length > 1 && e.lower.includes(phrase) ? e.lower.indexOf(phrase) : e.lower.indexOf(tokens[0]);
    const end = start + (tokens.length > 1 && e.lower.includes(phrase) ? phrase.length : tokens[0].length);
    const part = [...e.parts].reverse().find(p => p.start <= start) || e.parts[0];
    hits.push({ entry: e, start, end, x: part?.x ?? e.x, y: e.y, score });
  }
  hits.sort((a, b) => b.score - a.score || a.entry.label.localeCompare(b.entry.label, undefined, { numeric: true }) || a.entry.page - b.entry.page || b.y - a.y);
  return hits.slice(0, limit);
}

/** A short piece of the line around the match: { before, match, after }. */
export function snippet(hit, radius = 40) {
  const t = hit.entry.text;
  const from = Math.max(0, hit.start - radius), to = Math.min(t.length, hit.end + radius);
  return { before: (from > 0 ? "…" : "") + t.slice(from, hit.start), match: t.slice(hit.start, hit.end), after: t.slice(hit.end, to) + (to < t.length ? "…" : "") };
}

/**
 * Where a hit sits in the drawing's WORLD feet, given the sheet's scale and offset.
 * Page y grows up, world y grows down; ptsPerFt = page points per real foot.
 */
export function hitToWorld(hit, { imgX = 0, imgY = 0, ptsPerFt }) {
  if (!(ptsPerFt > 0)) return null;
  return { x: imgX + hit.x / ptsPerFt, y: imgY + (hit.entry.pageH - hit.y) / ptsPerFt };
}
